import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { medalTableService } from '../../services/medalTableService';

const MedalTableTailwind = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRegion, setSelectedRegion] = useState("all");
  const [sortConfig, setSortConfig] = useState({ key: "rank", direction: "asc" });
  const [medalData, setMedalData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Beispieldaten für bessere Visualisierung
  const sampleMedalData = [
    { id: 1, nation: "Deutschland", region: "Europe", flag: "/images/flags/DE.png", gold: 12, silver: 8, bronze: 15, total: 35 },
    { id: 2, nation: "China", region: "Asia", flag: "/images/flags/CN.png", gold: 18, silver: 12, bronze: 9, total: 39 },
    { id: 3, nation: "Japan", region: "Asia", flag: "/images/flags/JP.png", gold: 10, silver: 14, bronze: 11, total: 35 },
    { id: 4, nation: "USA", region: "America", flag: "/images/flags/US.png", gold: 8, silver: 10, bronze: 12, total: 30 },
    { id: 5, nation: "Frankreich", region: "Europe", flag: "/images/flags/FR.png", gold: 7, silver: 9, bronze: 8, total: 24 },
    { id: 6, nation: "Italien", region: "Europe", flag: "/images/flags/IT.png", gold: 6, silver: 7, bronze: 10, total: 23 },
    { id: 7, nation: "Brasilien", region: "America", flag: "/images/flags/BR.png", gold: 5, silver: 8, bronze: 6, total: 19 },
    { id: 8, nation: "Australien", region: "Oceania", flag: "/images/flags/AU.png", gold: 4, silver: 6, bronze: 7, total: 17 },
    { id: 9, nation: "Kanada", region: "America", flag: "/images/flags/CA.png", gold: 3, silver: 5, bronze: 8, total: 16 },
    { id: 10, nation: "Südkorea", region: "Asia", flag: "/images/flags/KR.png", gold: 9, silver: 4, bronze: 3, total: 16 },
    { id: 11, nation: "Großbritannien", region: "Europe", flag: "/images/flags/GB.png", gold: 2, silver: 4, bronze: 6, total: 12 },
    { id: 12, nation: "Russland", region: "Europe", flag: "/images/flags/RU.png", gold: 11, silver: 3, bronze: 2, total: 16 },
    { id: 13, nation: "Spanien", region: "Europe", flag: "/images/flags/ES.png", gold: 1, silver: 3, bronze: 5, total: 9 },
    { id: 14, nation: "Mexiko", region: "America", flag: "/images/flags/MX.png", gold: 0, silver: 2, bronze: 4, total: 6 },
    { id: 15, nation: "Südafrika", region: "Africa", flag: "/images/flags/ZA.png", gold: 0, silver: 1, bronze: 3, total: 4 }
  ];

  // Lade Medaillendaten von der API
  useEffect(() => {
    loadMedalData();
  }, []);

  const loadMedalData = async () => {
    try {
      setLoading(true);
      const data = await medalTableService.getMedalTable();
      if (data && data.length > 0) {
        setMedalData(data);
      } else {
        // Fallback zu Beispieldaten wenn API leer ist
        setMedalData(sampleMedalData);
      }
    } catch (error) {
      console.error('Fehler beim Laden des Medaillenspiegels:', error);
      // Fallback zu Beispieldaten bei Fehler
      setMedalData(sampleMedalData);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleRegionChange = (region) => {
    setSelectedRegion(region);
  };

  const regionOptions = [
    { value: "all", label: t("admin.medalTable.allRegions", "Alle Regionen") },
    { value: "Europe", label: t("admin.medalTable.europe", "Europa") },
    { value: "Asia", label: t("admin.medalTable.asia", "Asien") },
    { value: "America", label: t("admin.medalTable.america", "Amerika") },
    { value: "Oceania", label: t("admin.medalTable.oceania", "Ozeanien") },
    { value: "Africa", label: t("admin.medalTable.africa", "Afrika") }
  ];

  // Filter nach Suchbegriff und Region
  const filteredMedals = medalData.filter((item) => {
    const matchesSearch = item.nation.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRegion = selectedRegion === "all" || item.region === selectedRegion;
    return matchesSearch && matchesRegion;
  });

  // Sortierfunktion
  const sortKeys = {
    rank: (a, b) => a.rank - b.rank,
    nation: (a, b) => a.nation.localeCompare(b.nation),
    gold: (a, b) => b.gold - a.gold,
    silver: (a, b) => b.silver - a.silver,
    bronze: (a, b) => b.bronze - a.bronze,
    total: (a, b) => b.total - a.total
  };

  // Sortiere nach Gold, Silber, Bronze, Total (Standard für Rang)
  const sortedMedals = [...filteredMedals].sort((a, b) => {
    if (sortConfig.key === "rank") {
      if (a.gold !== b.gold) return b.gold - a.gold;
      if (a.silver !== b.silver) return b.silver - a.silver;
      if (a.bronze !== b.bronze) return b.bronze - a.bronze;
      return b.total - a.total;
    }
    const cmp = sortKeys[sortConfig.key](a, b);
    return sortConfig.direction === "asc" ? cmp : -cmp;
  });

  // Aktualisiere Ränge basierend auf Sortierung
  const medalsWithRanks = sortedMedals.map((nation, index) => ({
    ...nation,
    rank: index + 1
  }));

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? "▲" : "▼";
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1: return "🥇";
      case 2: return "🥈";
      case 3: return "🥉";
      default: return `#${rank}`;
    }
  };

  const getTotalMedals = () => {
    return medalsWithRanks.reduce((total, nation) => total + (nation.total || 0), 0);
  };

  const getTotalGold = () => {
    return medalsWithRanks.reduce((total, nation) => total + (nation.gold || 0), 0);
  };

  const getTotalSilver = () => {
    return medalsWithRanks.reduce((total, nation) => total + (nation.silver || 0), 0);
  };

  const getTotalBronze = () => {
    return medalsWithRanks.reduce((total, nation) => total + (nation.bronze || 0), 0);
  };

  if (loading) {
    return (
      <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Skeleton */}
          <div className="mb-8 animate-pulse">
            <div className="h-9 bg-slate-200 dark:bg-slate-700 rounded w-64 mb-2"></div>
            <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded w-96"></div>
          </div>

          {/* Controls Skeleton */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6 animate-pulse">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 h-[46px] bg-slate-200 dark:bg-slate-700 rounded-lg"></div>
            </div>
          </div>

          {/* Table Skeleton */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden animate-pulse">
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-slate-200 dark:divide-slate-700">
                <thead className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50">
                  <tr>
                    {[...Array(6)].map((_, i) => (
                      <th key={i} className="px-6 py-3">
                        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-20"></div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {[...Array(10)].map((_, i) => (
                    <tr key={i}>
                      {[...Array(6)].map((_, j) => (
                        <td key={j} className="px-6 py-4">
                          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-full"></div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                {t("admin.medalTable.title", "Medaillenspiegel")}
              </h1>
              <p className="text-slate-600 dark:text-slate-400 mt-2">
                {t("admin.medalTable.subtitle", "Übersicht über alle Medaillen und Nationen-Rankings.")}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">{medalsWithRanks.length}</div>
                <div className="text-sm text-slate-500 dark:text-slate-400">{t("admin.medalTable.nations", "Nationen")}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Controls Section */}
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder={t("admin.medalTable.search", "Nation suchen...")}
                value={searchTerm}
                onChange={handleSearch}
                className="w-full pl-10 pr-4 h-[46px] border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
              />
              <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 dark:text-slate-500 text-sm"></i>
            </div>
          </div>
        </div>

        {/* Filter Groups */}
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6">
          <div className="space-y-6">
            {/* Region Filter */}
            <div>
              <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                {t("admin.medalTable.region", "Region")}
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {regionOptions.map((option) => (
                  <button
                    key={option.value}
                    className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                      selectedRegion === option.value
                        ? 'bg-primary dark:bg-primary-light text-white'
                        : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                    }`}
                    onClick={() => handleRegionChange(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Medal Statistics */}
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-yellow-100 dark:bg-yellow-900/30 rounded-full mb-2">
                <span className="text-2xl">🥇</span>
              </div>
              <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">{getTotalGold()}</div>
              <div className="text-sm text-slate-500 dark:text-slate-400">{t("admin.medalTable.totalGold", "Gold")}</div>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full mb-2">
                <span className="text-2xl">🥈</span>
              </div>
              <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">{getTotalSilver()}</div>
              <div className="text-sm text-slate-500 dark:text-slate-400">{t("admin.medalTable.totalSilver", "Silber")}</div>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-full mb-2">
                <span className="text-2xl">🥉</span>
              </div>
              <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">{getTotalBronze()}</div>
              <div className="text-sm text-slate-500 dark:text-slate-400">{t("admin.medalTable.totalBronze", "Bronze")}</div>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-slate-100 dark:bg-slate-700 rounded-full mb-2">
                <span className="text-2xl">🏆</span>
              </div>
              <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">{getTotalMedals()}</div>
              <div className="text-sm text-slate-500 dark:text-slate-400">{t("admin.medalTable.totalMedals", "Gesamt")}</div>
            </div>
          </div>
        </div>

        {/* Medal Table */}
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
              <thead className="bg-slate-50 dark:bg-slate-700/50">
                <tr>
                  <th 
                    onClick={() => handleSort("rank")} 
                    className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                  >
                    {t("admin.medalTable.rank", "Rang")} {getSortIcon("rank")}
                  </th>
                  <th 
                    onClick={() => handleSort("nation")} 
                    className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                  >
                    {t("admin.medalTable.nation", "Nation")} {getSortIcon("nation")}
                  </th>
                  <th 
                    onClick={() => handleSort("gold")} 
                    className="px-6 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                  >
                    🥇 {t("admin.medalTable.gold", "Gold")} {getSortIcon("gold")}
                  </th>
                  <th 
                    onClick={() => handleSort("silver")} 
                    className="px-6 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                  >
                    🥈 {t("admin.medalTable.silver", "Silber")} {getSortIcon("silver")}
                  </th>
                  <th 
                    onClick={() => handleSort("bronze")} 
                    className="px-6 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                  >
                    🥉 {t("admin.medalTable.bronze", "Bronze")} {getSortIcon("bronze")}
                  </th>
                  <th 
                    onClick={() => handleSort("total")} 
                    className="px-6 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                  >
                    🏆 {t("admin.medalTable.total", "Gesamt")} {getSortIcon("total")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-700">
                {medalsWithRanks.map((nation, index) => (
                  <tr key={nation.id} className={index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-slate-50 dark:bg-slate-700/30'}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                          nation.rank === 1 ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300' :
                          nation.rank === 2 ? 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300' :
                          nation.rank === 3 ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300' :
                          'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                        }`}>
                          {getRankIcon(nation.rank)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <img
                          src={process.env.PUBLIC_URL + nation.flag}
                          alt={nation.nation}
                          className="w-8 h-8 object-cover rounded-full border border-slate-200 dark:border-slate-600 mr-3"
                        />
                        <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{nation.nation}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 rounded-full text-sm font-bold">
                        {nation.gold !== null ? nation.gold : '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 rounded-full text-sm font-bold">
                        {nation.silver !== null ? nation.silver : '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 rounded-full text-sm font-bold">
                        {nation.bronze !== null ? nation.bronze : '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-300 rounded-full text-sm font-bold">
                        {nation.total !== null ? nation.total : '-'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </main>
  );
};

export default MedalTableTailwind;
